import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAccess } from "@/contexts/AccessContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Compass, BookOpen } from "lucide-react";

interface ChallengeRow {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  price_cents: number | null;
  hero_image_url: string | null;
  color: string | null;
  icon: string | null;
}

const formatPrice = (cents: number | null) =>
  cents == null
    ? "—"
    : (cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const truncate = (s: string | null, n: number) => {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + "…";
};

export default function Themenreisen() {
  const access = useAccess();

  const { data: challenges = [], isLoading } = useQuery({
    queryKey: ["public-purchasable-challenges"],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("learning_challenges")
        .select("id, slug, title, description, price_cents, hero_image_url, color, icon")
        .eq("is_purchasable", true)
        .not("published_at", "is", null)
        .lte("published_at", now)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as ChallengeRow[];
    },
  });

  const { data: challengeLessons = [] } = useQuery({
    queryKey: ["public-challenge-lessons-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_lessons")
        .select("challenge_id, lesson_id");

      if (error) throw error;
      return data as { challenge_id: string; lesson_id: string }[];
    },
  });

  const lessonCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const cl of challengeLessons) {
      counts.set(cl.challenge_id, (counts.get(cl.challenge_id) || 0) + 1);
    }
    return counts;
  }, [challengeLessons]);

  const isPro = access.tier === "pro";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Themenreisen | MusiKonzept"
        description="Käufliche Themenreisen bei MusiKonzept — kuratierte Lernpfade aus verschiedenen Kursen."
        path="/themenreisen"
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm text-primary mb-4 border border-primary/20">
              <Compass className="w-4 h-4" />
              <span className="text-sm font-medium">Kuratierte Lernpfade</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Themenreisen
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
              Kuratierte Lernpfade aus unseren Kursen — wähle dein Thema und starte sofort.
            </p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : challenges.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground text-lg mb-2">
                Aktuell sind keine Themenreisen verfügbar.
              </p>
              <p className="text-muted-foreground">
                Schau bald wieder vorbei!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {challenges.map((challenge) => {
                const count = lessonCounts.get(challenge.id) || 0;
                const isEnrolled = access.challengeEnrollments.includes(challenge.id);

                return (
                  <Link
                    key={challenge.id}
                    to={`/themenreise/${challenge.slug}`}
                    className="group"
                  >
                    <Card className="overflow-hidden border-border/50 bg-card hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 h-full">
                      {/* Image Area */}
                      <div className="relative aspect-video overflow-hidden">
                        {challenge.hero_image_url ? (
                          <img
                            src={challenge.hero_image_url}
                            alt={challenge.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center"
                            style={{ backgroundColor: challenge.color || "hsl(var(--primary))" }}
                          >
                            <Compass className="w-16 h-16 text-white/80" />
                          </div>
                        )}

                        {/* Status Badges */}
                        <div className="absolute top-3 right-3 flex flex-col gap-2 items-end z-10">
                          {isEnrolled && (
                            <Badge className="bg-emerald-500 text-white border-0 shadow-md">
                              Bereits gekauft
                            </Badge>
                          )}
                        </div>
                      </div>

                      <CardContent className="p-5 flex flex-col">
                        <h3 className="font-semibold text-foreground text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {challenge.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
                          {truncate(challenge.description, 120)}
                        </p>
                        <div className="flex items-center justify-between pt-3 border-t border-border/50">
                          <span className="text-lg font-bold text-foreground">
                            {formatPrice(challenge.price_cents)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {count} {count === 1 ? "Lektion" : "Lektionen"}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
